import { format, addDays, startOfDay, differenceInDays, parseISO, isWeekend } from 'date-fns';
import WeeklyCapacityService from './WeeklyCapacityService.js';
import taskBatchingService from './TaskBatchingService.js';

/**
 * Recovery Plan Service
 *
 * Creates intelligent catch-up plans for overdue tasks
 * Maximally Assistive: Does the heavy lifting of planning and scheduling
 * Confidence-Instilling: Creates realistic, achievable plans
 */
class RecoveryPlanService {
  constructor() {
    // Default capacity if no user preferences available - realistic limits
    this.defaultDailyCapacity = {
      weekday: { min: 1.5, ideal: 2.5, max: 4 }, // hours
      weekend: { min: 2, ideal: 3.5, max: 5 }    // hours - reduced from 6 to 5
    };
    this.taskBatchingService = taskBatchingService;

    // Production safety features
    this.isProduction = (typeof window !== 'undefined' && window.process?.env?.NODE_ENV === 'production') || false;
    this.maxTasksPerPlan = 50; // Prevent memory issues with huge task lists
    this.maxDaysInPlan = 30; // Reasonable planning horizon
    this.enableDetailedLogging = !this.isProduction;
  }

  /**
   * Safe logging method that respects production environment
   */
  safeLog(level, message, data = null) {
    if (!this.enableDetailedLogging && level === 'debug') {
      return; // Skip debug logs in production
    }

    const logMethod = console[level] || console.log;
    const timestamp = new Date().toISOString();

    if (data) {
      logMethod(`[RecoveryPlanService] ${timestamp} - ${message}`, data);
    } else {
      logMethod(`[RecoveryPlanService] ${timestamp} - ${message}`);
    }
  }

  /**
   * Validate service limits to prevent performance issues
   */
  validateServiceLimits(overdueTasks, options = {}) {
    const errors = [];

    if (overdueTasks.length > this.maxTasksPerPlan) {
      errors.push(`Too many overdue tasks (${overdueTasks.length}). Maximum supported: ${this.maxTasksPerPlan}`);
    }

    const planHorizonDays = options.maxDays || this.maxDaysInPlan;
    if (planHorizonDays > this.maxDaysInPlan) {
      errors.push(`Plan horizon too long (${planHorizonDays} days). Maximum: ${this.maxDaysInPlan}`);
    }

    return { isValid: errors.length === 0, errors };
  }

  /**
   * Calculate daily capacity based on user preferences
   */
  calculateDailyCapacity(userPreferences = {}) {
    const weeklyCapacity = WeeklyCapacityService.calculateWeeklyCapacity(userPreferences);

    // Convert weekly to daily capacity
    // Assume 5 working days + 2 weekend days with different intensity
    const weekdayCapacity = weeklyCapacity.maxHoursPerWeek * 0.6 / 5; // 60% spread across weekdays
    const weekendCapacity = weeklyCapacity.maxHoursPerWeek * 0.4 / 2; // 40% spread across weekends

    return {
      weekday: {
        min: Math.max(0.5, weekdayCapacity * 0.5),
        ideal: weekdayCapacity,
        max: Math.min(6, weekdayCapacity * 1.5)
      },
      weekend: {
        min: Math.max(1, weekendCapacity * 0.5),
        ideal: weekendCapacity,
        max: Math.min(8, weekendCapacity * 1.5)
      },
      weeklyTotal: weeklyCapacity.maxHoursPerWeek,
      preferences: userPreferences
    };
  }

  /**
   * Validates input parameters for recovery plan creation
   * @param {Array} overdueTasks - Tasks that are overdue
   * @param {Array} upcomingTasks - Future scheduled tasks
   * @param {Object} userContext - User preferences and constraints
   * @returns {Object} Validation result with isValid flag and errors array
   */
  validateInputs(overdueTasks, upcomingTasks, userContext) {
    const errors = [];

    // Validate overdueTasks
    if (!Array.isArray(overdueTasks)) {
      errors.push('overdueTasks must be an array');
    } else {
      overdueTasks.forEach((task, index) => {
        if (!task || typeof task !== 'object') {
          errors.push(`overdueTasks[${index}] must be a valid task object`);
          return;
        }

        if (!task.id && !task.title) {
          errors.push(`overdueTasks[${index}] must have either id or title`);
        }

        if (task.scheduled_date && !this.isValidDate(task.scheduled_date)) {
          errors.push(`overdueTasks[${index}] has invalid scheduled_date: ${task.scheduled_date}`);
        }

        if (task.estimated_hours && (typeof task.estimated_hours !== 'number' || task.estimated_hours < 0)) {
          errors.push(`overdueTasks[${index}] has invalid estimated_hours: ${task.estimated_hours}`);
        }
      });
    }

    // Validate upcomingTasks
    if (!Array.isArray(upcomingTasks)) {
      errors.push('upcomingTasks must be an array');
    } else {
      upcomingTasks.forEach((task, index) => {
        if (!task || typeof task !== 'object') {
          errors.push(`upcomingTasks[${index}] must be a valid task object`);
          return;
        }

        if (!task.scheduled_date) {
          errors.push(`upcomingTasks[${index}] must have scheduled_date`);
        } else if (!this.isValidDate(task.scheduled_date)) {
          errors.push(`upcomingTasks[${index}] has invalid scheduled_date: ${task.scheduled_date}`);
        }
      });
    }

    // Validate userContext
    if (userContext && typeof userContext !== 'object') {
      errors.push('userContext must be an object');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validates if a date string is valid
   * @param {string} dateString - Date string to validate
   * @returns {boolean} True if valid date
   */
  isValidDate(dateString) {
    if (!dateString || typeof dateString !== 'string') return false;
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date);
  }

  /**
   * Creates a comprehensive recovery plan for overdue tasks
   * @param {Array} overdueTasks - Tasks that are overdue
   * @param {Array} upcomingTasks - Future scheduled tasks
   * @param {Object} userContext - User preferences and constraints
   * @returns {Object} Recovery plan with redistributed tasks
   */
  createRecoveryPlan(overdueTasks, upcomingTasks = [], userContext = {}) {
    this.safeLog('info', 'Starting recovery plan creation', {
      overdueCount: overdueTasks.length,
      upcomingCount: upcomingTasks.length
    });

    try {
      // Comprehensive input validation
      const validationResult = this.validateInputs(overdueTasks, upcomingTasks, userContext);
      if (!validationResult.isValid) {
        throw new Error(`Recovery plan validation failed: ${validationResult.errors.join(', ')}`);
      }

      // Service limits validation for production safety
      const limitsResult = this.validateServiceLimits(overdueTasks, userContext.options);
      if (!limitsResult.isValid) {
        throw new Error(`Service limits exceeded: ${limitsResult.errors.join(', ')}`);
      }

      const now = new Date();

    // Calculate user-specific daily capacity
    const dailyCapacity = this.calculateDailyCapacity(userContext.userPreferences || {});

    const plan = {
      id: `recovery-${Date.now()}`,
      created: now,
      summary: {
        totalOverdue: overdueTasks.length,
        totalHours: 0,
        daysToRecover: 0,
        criticalTasks: 0,
        strategy: '',
        weeklyCapacity: dailyCapacity.weeklyTotal
      },
      phases: [],
      redistributedTasks: [],
      dailySchedule: {},
      warnings: [],
      successMetrics: {},
      dailyCapacity
    };

    if (overdueTasks.length === 0) {
      plan.summary.strategy = 'No overdue tasks - you\'re on track!';
      return plan;
    }

    // Analyze task characteristics
    const taskAnalysis = this.analyzeOverdueTasks(overdueTasks);
    plan.summary = { ...plan.summary, ...taskAnalysis };

    // Identify batchable tasks for more efficient recovery
    const batchingAnalysis = this.identifyBatchableOverdueTasks(overdueTasks);
    plan.batchingInfo = {
      hasBatches: batchingAnalysis.hasBatches,
      batchCount: batchingAnalysis.batches.length,
      individualTaskCount: batchingAnalysis.individualTasks.length
    };

    // Determine recovery strategy based on workload and user capacity
    const strategy = this.determineRecoveryStrategy(taskAnalysis, userContext, dailyCapacity);
    plan.summary.strategy = strategy.description;

    // Create phased recovery plan
    plan.phases = this.createPhasedPlan(overdueTasks, strategy);

    // Redistribute tasks across available days (including batches)
    const redistribution = this.redistributeTasks(
      batchingAnalysis.batches,
      batchingAnalysis.individualTasks,
      upcomingTasks,
      strategy,
      userContext,
      dailyCapacity
    );

    plan.redistributedTasks = redistribution.tasks;
    plan.dailySchedule = redistribution.schedule;
    plan.summary.daysToRecover = redistribution.daysNeeded;

    // Calculate success metrics
    plan.successMetrics = this.calculateSuccessMetrics(plan);

      // Add any warnings or considerations
      plan.warnings = this.generateWarnings(plan, upcomingTasks);

      this.safeLog('info', 'Recovery plan created successfully', {
        planId: plan.id,
        tasksRedistributed: plan.redistributedTasks.length,
        daysToRecover: plan.summary.daysToRecover
      });

      return plan;

    } catch (error) {
      this.safeLog('error', 'Failed to create recovery plan', {
        error: error.message,
        stack: this.enableDetailedLogging ? error.stack : undefined
      });

      // Re-throw with production-safe message
      if (this.isProduction) {
        throw new Error('Unable to create recovery plan. Please check your task data and try again.');
      } else {
        throw error;
      }
    }
  }

  /**
   * Analyze overdue tasks to understand the situation
   */
  analyzeOverdueTasks(tasks) {
    const now = new Date();
    const analysis = {
      totalHours: 0,
      criticalTasks: 0,
      categories: {},
      oldestOverdue: null,
      averageDaysOverdue: 0
    };

    let totalDaysOverdue = 0;
    let oldestDate = now;

    tasks.forEach(task => {
      // Calculate hours
      analysis.totalHours += task.estimated_hours || 0.75;

      // Count critical tasks
      if (task.priority === 'high' || task.priority === 'critical') {
        analysis.criticalTasks++;
      }

      // Group by category
      const category = task.category || 'other';
      if (!analysis.categories[category]) {
        analysis.categories[category] = { count: 0, hours: 0 };
      }
      analysis.categories[category].count++;
      analysis.categories[category].hours += task.estimated_hours || 0.75;

      // Find oldest overdue
      if (task.scheduled_date) {
        const taskDate = parseISO(task.scheduled_date);
        const daysOverdue = differenceInDays(now, taskDate);
        totalDaysOverdue += daysOverdue;

        if (taskDate < oldestDate) {
          oldestDate = taskDate;
          analysis.oldestOverdue = task;
        }
      }
    });

    analysis.averageDaysOverdue = tasks.length > 0
      ? Math.round(totalDaysOverdue / tasks.length)
      : 0;

    return analysis;
  }

  /**
   * Determine the best recovery strategy based on workload and user capacity
   */
  determineRecoveryStrategy(analysis, userContext = {}, dailyCapacity = null) {
    const { totalHours, criticalTasks, averageDaysOverdue } = analysis;

    // Get user capacity to inform strategy selection
    const capacity = dailyCapacity || this.calculateDailyCapacity(userContext.userPreferences || {});
    const weeklyCapacity = capacity.weeklyTotal;

    // Strategies optimized for college application deadlines
    const strategies = {
      sprint: {
        name: 'sprint',
        description: 'Intensive 3-day sprint to clear critical backlog',
        daysTarget: 3,
        dailyHours: 4,
        priorityFocus: true,
        suitable: totalHours <= 12 && criticalTasks >= 2
      },
      steady: {
        name: 'steady',
        description: 'Focused 5-day recovery with sustained effort',
        daysTarget: 5,
        dailyHours: 3,
        priorityFocus: true,
        suitable: totalHours > 12 && totalHours <= 20
      },
      intensive: {
        name: 'intensive',
        description: 'Comprehensive 7-day intensive recovery',
        daysTarget: 7,
        dailyHours: 3.5,
        priorityFocus: true,
        suitable: totalHours > 20 && totalHours <= 35
      },
      extended: {
        name: 'extended',
        description: 'Maximum 10-day recovery with balanced workload',
        daysTarget: 10,
        dailyHours: 2.5,
        priorityFocus: false,
        suitable: totalHours > 35
      }
    };

    // Select appropriate strategy based on workload, urgency, and user capacity
    const canHandleIntensive = weeklyCapacity >= 12; // User has high weekly capacity
    const hasUrgentDeadlines = criticalTasks >= 2 || averageDaysOverdue > 7;

    if (userContext.preferredPace === 'aggressive' && totalHours <= weeklyCapacity * 1.5) {
      return strategies.sprint;
    } else if (hasUrgentDeadlines && totalHours <= weeklyCapacity && canHandleIntensive) {
      return strategies.sprint;
    } else if (totalHours <= weeklyCapacity * 1.2) {
      return strategies.steady;
    } else if (totalHours <= weeklyCapacity * 2 && canHandleIntensive) {
      return strategies.intensive;
    } else {
      return strategies.extended;
    }
  }

  /**
   * Identify and group batchable overdue tasks for more efficient recovery
   */
  identifyBatchableOverdueTasks(overdueTasks) {
    // Mark overdue tasks as temporarily non-overdue to allow batching analysis
    const tasksForBatching = overdueTasks.map(task => ({ ...task, isOverdue: false }));

    // Use task batching service to identify batchable groups
    const { batchable, nonBatchable } = this.taskBatchingService.separateTasks(tasksForBatching);

    if (batchable.length < 3) {
      // Not enough tasks to create meaningful batches
      return {
        batches: [],
        individualTasks: overdueTasks,
        hasBatches: false
      };
    }

    // Create batches from batchable tasks
    const batches = this.taskBatchingService.createDynamicBatches(batchable, new Date(), {
      maxBatchSize: 4, // Smaller batches for recovery planning
      minBatchSize: 2,
      preferredBatchSize: 3,
      respectDeadlines: false, // These are already overdue
      groupBySimilarity: true
    });

    return {
      batches: batches.filter(item => item.isBatch), // Only actual batches
      individualTasks: [
        ...nonBatchable.map(task => ({ ...task, isOverdue: true })), // Restore overdue flag
        ...batches.filter(item => !item.isBatch).map(task => ({ ...task, isOverdue: true }))
      ],
      hasBatches: batches.some(item => item.isBatch)
    };
  }

  /**
   * Create phased recovery plan
   */
  createPhasedPlan(tasks, strategy) {
    const phases = [];

    // Sort tasks by priority and age
    const sortedTasks = this.prioritizeTasks(tasks);

    if (strategy.name === 'sprint') {
      // Single intensive phase
      phases.push({
        name: 'Sprint Recovery',
        duration: '3 days',
        focus: 'Clear all critical and high-priority tasks',
        tasks: sortedTasks.slice(0, Math.ceil(sortedTasks.length * 0.7)),
        dailyTarget: strategy.dailyHours
      });

      if (sortedTasks.length > phases[0].tasks.length) {
        phases.push({
          name: 'Completion',
          duration: '2 days',
          focus: 'Finish remaining lower-priority items',
          tasks: sortedTasks.slice(phases[0].tasks.length),
          dailyTarget: 1.5
        });
      }
    } else if (strategy.name === 'steady') {
      // Two phases: critical first, then remainder
      const criticalTasks = sortedTasks.filter(t =>
        t.priority === 'high' || t.priority === 'critical'
      );
      const normalTasks = sortedTasks.filter(t =>
        t.priority !== 'high' && t.priority !== 'critical'
      );

      phases.push({
        name: 'Priority Recovery',
        duration: '2 days',
        focus: 'Address critical and blocking tasks',
        tasks: criticalTasks,
        dailyTarget: strategy.dailyHours
      });

      phases.push({
        name: 'Standard Recovery',
        duration: '3 days',
        focus: 'Complete remaining tasks systematically',
        tasks: normalTasks,
        dailyTarget: strategy.dailyHours
      });
    } else if (strategy.name === 'intensive') {
      // Three phases for comprehensive recovery
      const criticalTasks = sortedTasks.filter(t =>
        t.priority === 'high' || t.priority === 'critical'
      );
      const normalTasks = sortedTasks.filter(t =>
        t.priority === 'normal'
      );
      const lowTasks = sortedTasks.filter(t =>
        t.priority === 'low'
      );

      phases.push({
        name: 'Critical Phase',
        duration: '2 days',
        focus: 'Handle all critical and high-priority items',
        tasks: criticalTasks,
        dailyTarget: strategy.dailyHours
      });

      phases.push({
        name: 'Core Recovery',
        duration: '3 days',
        focus: 'Systematic completion of normal priority tasks',
        tasks: normalTasks,
        dailyTarget: strategy.dailyHours
      });

      phases.push({
        name: 'Final Clearance',
        duration: '2 days',
        focus: 'Complete remaining low-priority items',
        tasks: lowTasks,
        dailyTarget: 2
      });
    } else {
      // Extended: Four phases for maximum timeline
      const taskChunks = this.chunkTasks(sortedTasks, 4);

      phases.push({
        name: 'Urgent Items',
        duration: '2 days',
        focus: 'Address most critical overdue tasks',
        tasks: taskChunks[0],
        dailyTarget: strategy.dailyHours
      });

      phases.push({
        name: 'Priority Recovery',
        duration: '3 days',
        focus: 'Work through high-priority backlog',
        tasks: taskChunks[1],
        dailyTarget: strategy.dailyHours
      });

      phases.push({
        name: 'Standard Items',
        duration: '3 days',
        focus: 'Complete normal priority tasks',
        tasks: taskChunks[2],
        dailyTarget: strategy.dailyHours
      });

      phases.push({
        name: 'Final Items',
        duration: '2 days',
        focus: 'Finish remaining low-priority tasks',
        tasks: taskChunks[3],
        dailyTarget: 2
      });
    }

    return phases;
  }

  /**
   * Redistribute tasks across available days (including batches)
   */
  redistributeTasks(batches, individualTasks, upcomingTasks, strategy, userContext = {}, dailyCapacity = null) {
    const now = startOfDay(new Date());
    const schedule = {};
    const redistributedTasks = [];
    let daysUsed = 0;

    // Use provided capacity or fallback to default
    const capacity = dailyCapacity || this.calculateDailyCapacity(userContext.userPreferences || {});

    // Combine and sort both batches and individual tasks by priority
    const allItemsToSchedule = [
      ...batches.map(batch => ({ ...batch, isBatch: true, priority: this.calculateBatchPriority(batch) })),
      ...individualTasks
    ];
    const sortedItems = this.prioritizeItems(allItemsToSchedule);

    // Track daily capacity and existing tasks
    const dailyLoads = {};
    const existingTasksByDate = {};

    // First, map existing upcoming tasks to understand current load
    upcomingTasks.forEach(task => {
      if (task.scheduled_date) {
        const dateKey = format(parseISO(task.scheduled_date), 'yyyy-MM-dd');
        if (!dailyLoads[dateKey]) {
          dailyLoads[dateKey] = 0;
        }
        if (!existingTasksByDate[dateKey]) {
          existingTasksByDate[dateKey] = [];
        }

        dailyLoads[dateKey] += task.estimated_hours || 0.75;
        existingTasksByDate[dateKey].push({
          title: task.title,
          estimated_hours: task.estimated_hours || 0.75,
          description: task.description || 'Already scheduled commitment',
          category: task.category,
          priority: task.priority,
          isFixed: true
        });
      }
    });

    // No mock data in production - only use real timeline data

    // Redistribute overdue items (tasks and batches) with strict timeline limits
    const maxRecoveryDays = Math.min(strategy.daysTarget + 2, 12); // Hard cap at 12 days

    sortedItems.forEach(item => {
      let scheduled = false;
      let dayOffset = 0;

      // Try to schedule within the recovery period limit
      while (!scheduled && dayOffset < maxRecoveryDays) {
        const targetDate = addDays(now, dayOffset);
        const dateKey = format(targetDate, 'yyyy-MM-dd');
        const isWeekendDay = isWeekend(targetDate);
        const dayCapacity = isWeekendDay
          ? capacity.weekend
          : capacity.weekday;

        // Check if this day has capacity
        const currentLoad = dailyLoads[dateKey] || 0;
        const itemHours = item.isBatch
          ? item.tasks.reduce((sum, t) => sum + (t.estimated_hours || 0.75), 0)
          : item.estimated_hours || 0.75;
        // Limit capacity to realistic levels (max 6 hours even in aggressive mode)
        const maxCapacity = userContext.aggressive
          ? Math.min(dayCapacity.max, 6)
          : Math.min(dayCapacity.ideal, 4);

        if (currentLoad + itemHours <= maxCapacity) {
          if (item.isBatch) {
            // Schedule the entire batch
            const batchTasks = item.tasks.map(task => ({
              ...task,
              scheduled_date: dateKey,
              original_date: task.scheduled_date,
              recovery_scheduled: true,
              recovery_phase: this.determinePhase(dayOffset, strategy),
              batch_id: item.id,
              batch_name: item.name,
              reason: `Batched with ${item.tasks.length - 1} similar tasks for efficiency`
            }));

            redistributedTasks.push(...batchTasks);

            // Update schedule
            if (!schedule[dateKey]) {
              const existingTasks = existingTasksByDate[dateKey] || [];
              const existingHours = existingTasks.reduce((sum, task) => sum + (task.estimated_hours || 0), 0);

              schedule[dateKey] = {
                date: targetDate,
                tasks: [],
                existingTasks: existingTasks,
                totalHours: existingHours, // Start with existing task hours
                isWeekend: isWeekendDay
              };
            }

            schedule[dateKey].tasks.push(...batchTasks);
          } else {
            // Schedule individual task
            const newTask = {
              ...item,
              scheduled_date: dateKey,
              original_date: item.scheduled_date,
              recovery_scheduled: true,
              recovery_phase: this.determinePhase(dayOffset, strategy)
            };

            redistributedTasks.push(newTask);

            // Update schedule
            if (!schedule[dateKey]) {
              const existingTasks = existingTasksByDate[dateKey] || [];
              const existingHours = existingTasks.reduce((sum, task) => sum + (task.estimated_hours || 0), 0);

              schedule[dateKey] = {
                date: targetDate,
                tasks: [],
                existingTasks: existingTasks,
                totalHours: existingHours, // Start with existing task hours
                isWeekend: isWeekendDay
              };
            }

            // Add reason for scheduling this task here
            newTask.reason = this.generateReschedulingReason(item, currentLoad, maxCapacity, strategy);

            schedule[dateKey].tasks.push(newTask);
          }

          schedule[dateKey].totalHours += itemHours;

          // Update daily load
          dailyLoads[dateKey] = currentLoad + itemHours;

          scheduled = true;
          daysUsed = Math.max(daysUsed, dayOffset);
        }

        dayOffset++;
      }

      if (!scheduled) {
        // If we can't fit within recovery period, force schedule with higher capacity
        const fallbackDay = Math.min(dayOffset - 1, maxRecoveryDays - 1);
        const fallbackDate = addDays(now, fallbackDay);
        const dateKey = format(fallbackDate, 'yyyy-MM-dd');

        if (item.isBatch) {
          // Force schedule batch
          const batchTasks = item.tasks.map(task => ({
            ...task,
            scheduled_date: dateKey,
            original_date: task.scheduled_date,
            recovery_scheduled: true,
            batch_id: item.id,
            batch_name: item.name,
            warning: 'High-capacity day - batch scheduled with extended work session'
          }));

          redistributedTasks.push(...batchTasks);

          // Force add to schedule
          if (!schedule[dateKey]) {
            schedule[dateKey] = {
              date: fallbackDate,
              tasks: [],
              existingTasks: existingTasksByDate[dateKey] || [],
              totalHours: 0,
              isWeekend: isWeekend(fallbackDate)
            };
          }

          schedule[dateKey].tasks.push(...batchTasks);
          schedule[dateKey].totalHours += item.tasks.reduce((sum, t) => sum + (t.estimated_hours || 0.75), 0);
        } else {
          // Force schedule individual task
          const newTask = {
            ...item,
            scheduled_date: dateKey,
            original_date: item.scheduled_date,
            recovery_scheduled: true,
            warning: 'High-capacity day - may require extended work session'
          };

          redistributedTasks.push(newTask);

          // Force add to schedule
          if (!schedule[dateKey]) {
            schedule[dateKey] = {
              date: fallbackDate,
              tasks: [],
              existingTasks: existingTasksByDate[dateKey] || [],
              totalHours: 0,
              isWeekend: isWeekend(fallbackDate)
            };
          }

          schedule[dateKey].tasks.push(newTask);
          schedule[dateKey].totalHours += item.estimated_hours || 0.75;
        }

        daysUsed = Math.max(daysUsed, fallbackDay);
      }
    });

    return {
      tasks: redistributedTasks,
      schedule,
      daysNeeded: daysUsed
    };
  }

  /**
   * Calculate priority for a batch based on its constituent tasks
   */
  calculateBatchPriority(batch) {
    if (!batch.tasks || batch.tasks.length === 0) return 'normal';

    const priorities = batch.tasks.map(t => t.priority || 'normal');
    const priorityScores = {
      critical: 4,
      high: 3,
      normal: 2,
      low: 1
    };

    // Use the highest priority task in the batch
    const highestScore = Math.max(...priorities.map(p => priorityScores[p] || 2));
    const priorityMap = { 4: 'critical', 3: 'high', 2: 'normal', 1: 'low' };
    return priorityMap[highestScore] || 'normal';
  }

  /**
   * Prioritize mixed items (tasks and batches) for recovery scheduling
   */
  prioritizeItems(items) {
    return items.sort((a, b) => {
      // Priority levels
      const priorityScore = {
        critical: 4,
        high: 3,
        normal: 2,
        low: 1
      };

      const scoreA = priorityScore[a.priority] || 2;
      const scoreB = priorityScore[b.priority] || 2;

      if (scoreA !== scoreB) {
        return scoreB - scoreA;
      }

      // Prefer batches over individual tasks at same priority (efficiency)
      if (a.isBatch && !b.isBatch) return -1;
      if (!a.isBatch && b.isBatch) return 1;

      // For tasks, use existing priority logic
      if (!a.isBatch && !b.isBatch) {
        // Then by age (older first)
        if (a.scheduled_date && b.scheduled_date) {
          return parseISO(a.scheduled_date) - parseISO(b.scheduled_date);
        }
      }

      return 0;
    });
  }

  /**
   * Prioritize tasks for recovery scheduling
   */
  prioritizeTasks(tasks) {
    return tasks.sort((a, b) => {
      // Priority levels
      const priorityScore = {
        critical: 4,
        high: 3,
        normal: 2,
        low: 1
      };

      const scoreA = priorityScore[a.priority] || 2;
      const scoreB = priorityScore[b.priority] || 2;

      if (scoreA !== scoreB) {
        return scoreB - scoreA;
      }

      // Then by age (older first)
      if (a.scheduled_date && b.scheduled_date) {
        return parseISO(a.scheduled_date) - parseISO(b.scheduled_date);
      }

      // Then by category importance
      const categoryPriority = {
        testing: 5,
        essay: 4,
        financial: 3,
        research: 2,
        admin: 1
      };

      const catA = categoryPriority[a.category] || 0;
      const catB = categoryPriority[b.category] || 0;

      return catB - catA;
    });
  }

  /**
   * Find earliest available date with capacity
   */
  findEarliestAvailableDate(dailyLoads, task, dynamicCapacity = null) {
    const now = startOfDay(new Date());
    let testDate = now;
    const maxDays = 14; // Reduced from 30 to match our new timeline limits

    // Use provided capacity or fallback to default
    const capacity = dynamicCapacity || this.calculateDailyCapacity({});

    for (let i = 0; i < maxDays; i++) {
      const dateKey = format(testDate, 'yyyy-MM-dd');
      const isWeekendDay = isWeekend(testDate);
      const dayCapacity = isWeekendDay
        ? capacity.weekend.ideal
        : capacity.weekday.ideal;

      const currentLoad = dailyLoads[dateKey] || 0;
      const taskHours = task.estimated_hours || 0.75;

      if (currentLoad + taskHours <= dayCapacity) {
        return testDate;
      }

      testDate = addDays(testDate, 1);
    }

    // Fallback to 14 days out (max recovery period)
    return addDays(now, 14);
  }

  /**
   * Determine which phase a day falls into
   */
  determinePhase(dayNumber, strategy) {
    if (strategy.name === 'sprint') {
      return dayNumber <= 2 ? 1 : 2;
    } else if (strategy.name === 'steady') {
      return dayNumber <= 1 ? 1 : 2;
    } else if (strategy.name === 'intensive') {
      if (dayNumber <= 1) return 1;
      if (dayNumber <= 4) return 2;
      return 3;
    } else {
      // Extended strategy
      if (dayNumber <= 1) return 1;
      if (dayNumber <= 4) return 2;
      if (dayNumber <= 7) return 3;
      return 4;
    }
  }

  /**
   * Calculate success metrics for the plan
   */
  calculateSuccessMetrics(plan) {
    const metrics = {
      tasksPerDay: 0,
      averageHoursPerDay: 0,
      criticalClearTime: null,
      completionConfidence: 0,
      workloadBalance: 0
    };

    const dailyTasks = Object.values(plan.dailySchedule);
    if (dailyTasks.length > 0) {
      // Tasks per day
      const totalTasks = dailyTasks.reduce((sum, day) => sum + day.tasks.length, 0);
      metrics.tasksPerDay = (totalTasks / dailyTasks.length).toFixed(1);

      // Average hours per day
      const totalHours = dailyTasks.reduce((sum, day) => sum + day.totalHours, 0);
      metrics.averageHoursPerDay = (totalHours / dailyTasks.length).toFixed(1);

      // Critical clear time
      const criticalTasks = plan.redistributedTasks.filter(t =>
        t.priority === 'high' || t.priority === 'critical'
      );
      if (criticalTasks.length > 0) {
        const latestCritical = criticalTasks.reduce((latest, task) => {
          const taskDate = parseISO(task.scheduled_date);
          return taskDate > latest ? taskDate : latest;
        }, new Date());

        metrics.criticalClearTime = differenceInDays(latestCritical, new Date()) + ' days';
      }

      // Completion confidence (based on workload distribution)
      const maxDaily = Math.max(...dailyTasks.map(d => d.totalHours));
      if (maxDaily <= 3) {
        metrics.completionConfidence = 95;
      } else if (maxDaily <= 4) {
        metrics.completionConfidence = 85;
      } else if (maxDaily <= 5) {
        metrics.completionConfidence = 70;
      } else {
        metrics.completionConfidence = 50;
      }

      // Workload balance (standard deviation)
      const mean = totalHours / dailyTasks.length;
      const variance = dailyTasks.reduce((sum, day) => {
        return sum + Math.pow(day.totalHours - mean, 2);
      }, 0) / dailyTasks.length;
      const stdDev = Math.sqrt(variance);

      // Convert to balance score (100 = perfect balance, 0 = very unbalanced)
      metrics.workloadBalance = Math.max(0, Math.min(100, 100 - (stdDev * 30)));
    }

    return metrics;
  }

  /**
   * Generate warnings and considerations
   */
  generateWarnings(plan, upcomingTasks) {
    const warnings = [];

    // Check for overloaded days
    Object.entries(plan.dailySchedule).forEach(([date, day]) => {
      if (day.totalHours > 4) {
        warnings.push({
          type: 'high_load',
          message: `${format(parseISO(date), 'MMM d')} has ${day.totalHours.toFixed(1)} hours scheduled - consider spreading the load`,
          severity: 'medium'
        });
      }
    });

    // Check for conflicts with existing high-priority tasks
    const highPriorityUpcoming = upcomingTasks.filter(t =>
      t.priority === 'high' || t.priority === 'critical'
    );

    if (highPriorityUpcoming.length > 0) {
      warnings.push({
        type: 'priority_conflict',
        message: `You have ${highPriorityUpcoming.length} high-priority upcoming tasks that may need attention`,
        severity: 'low'
      });
    }

    // Check if recovery extends too long
    if (plan.summary.daysToRecover > 10) {
      warnings.push({
        type: 'extended_recovery',
        message: 'Recovery plan approaching maximum timeline - consider prioritizing only critical tasks',
        severity: 'high'
      });
    }

    return warnings;
  }

  /**
   * Chunk tasks into groups
   */
  chunkTasks(tasks, numberOfChunks) {
    const chunks = [];
    const chunkSize = Math.ceil(tasks.length / numberOfChunks);

    for (let i = 0; i < numberOfChunks; i++) {
      chunks.push(tasks.slice(i * chunkSize, (i + 1) * chunkSize));
    }

    return chunks.filter(chunk => chunk.length > 0);
  }

  /**
   * Generate a reason for why a task is being rescheduled to a specific day
   */
  generateReschedulingReason(task, currentLoad, maxCapacity, strategy) {
    const remainingCapacity = maxCapacity - currentLoad;
    const taskHours = task.estimated_hours || 0.75;

    if (task.priority === 'critical') {
      return `Critical deadline, ${remainingCapacity.toFixed(1)}h capacity available`;
    } else if (task.priority === 'high') {
      return `High priority, needs focused time block`;
    } else if (strategy.name === 'sprint') {
      return `Sprint mode: bundled with other urgent items`;
    } else if (remainingCapacity >= taskHours * 2) {
      return `Light day, good opportunity to catch up`;
    } else if (taskHours <= 0.5) {
      return `Quick task, fits in remaining time`;
    } else {
      return `Optimal fit with ${remainingCapacity.toFixed(1)}h available capacity`;
    }
  }

  /**
   * Apply a recovery plan to the actual timeline
   */
  async applyRecoveryPlan(plan, updateFunction) {
    const results = {
      success: [],
      failed: [],
      totalUpdated: 0
    };

    // Apply each task update
    for (const task of plan.redistributedTasks) {
      try {
        await updateFunction(task.id, {
          scheduled_date: task.scheduled_date,
          metadata: {
            ...task.metadata,
            recovery_planned: true,
            original_overdue_date: task.original_date
          }
        });

        results.success.push(task.id);
        results.totalUpdated++;
      } catch (error) {
        results.failed.push({
          taskId: task.id,
          error: error.message
        });
      }
    }

    return results;
  }
}

export default new RecoveryPlanService();